// Bank feeds (Plaid): connect a bank, map each bank account to an eloNest
// account, sync, and compare the bank's live balance with the ledger.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { accountsAPI } from '../services/api';
import { bankFeedsAPI, startLink, takeFlash, balanceStatus, ConnectBankButton, MappingSelect, mappingPayload, businessOf, PlanNotice, pausedText, syncButton } from '../features/bank-feeds';
import { readSavedBusiness, ALL } from '../utils/businessSelection';
import { formatApiError } from '../utils/errorMessage';
import { autocatAPI } from '../features/autocat';
import { canViewBankFeeds, canManageBankFeeds } from '../features/bank-feeds/bankFeedPermissions';
import MatchReviewCard from '../features/bank-feeds/MatchReviewCard';
import { disconnectMessage } from '../features/bank-feeds/disconnectMessage';
import { syncHealth, gapBreakdown, nextStep, lastSyncLine } from '../features/bank-feeds/feedHealth';
import { choiceText } from '../features/bank-feeds/MappingSelect';
import ConfirmDisconnect from '../features/bank-feeds/ConfirmDisconnect';
import { connectionButton } from '../features/bank-feeds/connectionButton';

const TONE = { ok: '#15803d', warn: '#b45309', bad: '#b91c1c', muted: '#6b7280' };

const money = (v) => (v === null || v === undefined ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

export function syncSummary(d) {
  const parts = [`Synced: ${d.added || 0} new, ${d.modified || 0} updated, ${d.removed || 0} removed, ${d.pending || 0} pending`];
  if (d.locked) parts.push(`${d.locked} on reconciled rows left unchanged`);
  if (d.not_imported) parts.push(`${d.not_imported} on accounts you chose not to import`);
  // Always shown (also when zero) so a sync visibly ran the duplicate and balance checks.
  parts.push(`${d.linked || 0} matched to rows already in eloNest`);
  parts.push(`${d.match_review || 0} possible duplicate${d.match_review === 1 ? '' : 's'} to review`);
  let text = `${parts.join(', ')}.`;
  for (const s of d.starting_balances || []) {
    text += ` Starting balance set from the bank: ${money(s.opening_balance)} on ${s.opening_balance_date}; check it on the Reconciliation page.`;
  }
  const review = d.starting_balance_review || [];
  const mismatches = d.balance_mismatches || [];
  if (d.balances_checked !== undefined) {
    text += mismatches.length ? '' : ` Bank vs eloNest: ${d.balances_checked} account${d.balances_checked === 1 ? '' : 's'} checked, all match.`;
  }
  for (const m of mismatches) {
    text += ` Account ${m.account_id}: bank ${money(m.bank_balance)}, eloNest ${money(m.elonest_balance)} (off by ${money(m.difference)}).`;
  }
  if (review.length) text += ` ${review.length} starting balance${review.length === 1 ? '' : 's'} need${review.length === 1 ? 's' : ''} review (below).`;
  return text;
}

export const REVIEW_REASON = {
  negative_start: 'would start below zero on a checking or savings account',
  predates_bank_history: 'the account has rows older than the bank history',
  anchor_predates_bank: 'the bank sent rows dated on or before the starting balance you set',
  reconciled_rows_before_start: 'reconciled rows sit before the current start (cannot move it here)',
  closed_period: 'the books are closed for that period (cannot move it here)',
  provisional: 'it is marked provisional',
  transfers_need_direction: 'some older transfers on the account need an In/Out direction first',
};

// The possible-duplicates list is capped; say so when the cap hides rows.
export function matchCapText(info, shown) {
  if (!info || !(Number(info.total) > shown)) return '';
  const hidden = Number(info.total) - shown;
  return `Showing ${shown} of ${info.total}. ${hidden} more ${hidden === 1 ? 'is' : 'are'} hidden by the ${info.limit || shown}-row limit`
    + `${info.item_id ? '' : '; pick a bank above to see each bank\'s own list'}. Resolve these to see the rest.`;
}

// What Bank Feeds says when the list is empty: loading, nothing anywhere, or
// connections that exist only in the user's other businesses.
export function emptyText({ loaded, count, elsewhere }) {
  if (!loaded) return 'Loading bank connections...';
  if (count > 0) return '';
  if (elsewhere > 0) {
    return `No banks are connected for this business. You have ${elsewhere} bank connection${elsewhere === 1 ? '' : 's'} in your other businesses; choose All businesses at the top to see ${elsewhere === 1 ? 'it' : 'them'}.`;
  }
  return 'No banks connected yet.';
}

// Polls the connection list until the Item's last sync time changes or it
// records an error. Returns the Item, or null if it is still running.
export async function waitForSync(listItems, itemId, before, { tries = 36, everyMs = 5000, sleep = (ms) => new Promise((r) => setTimeout(r, ms)) } = {}) {
  for (let i = 0; i < tries; i += 1) {
    await sleep(everyMs);
    const r = await listItems().catch(() => null);
    const it = r && (r.data.items || []).find((x) => Number(x.id) === Number(itemId));
    if (it && ((it.last_synced_at || null) !== before || (it.status === 'error' && it.last_error))) return it;
  }
  return null;
}

export default function BankFeeds({ selectedBusiness }) {
  const saved = selectedBusiness ?? readSavedBusiness();
  const businessId = saved && saved !== ALL ? Number(saved) : null;
  const [status, setStatus] = useState(null);
  const [disconnecting, setDisconnecting] = useState(null);
  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [hints, setHints] = useState({});
  // Running actions by key. Keys "item:<id>:..." belong to one bank and only
  // disable that bank's buttons; other keys (page-wide actions) disable all.
  const [busyKeys, setBusyKeys] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [elsewhere, setElsewhere] = useState(0);
  // Per bank: this session's last sync or action result.
  const [results, setResults] = useState({});
  // Mapping picks waiting for Confirm (bank account id -> choice).
  const [pending, setPending] = useState({});
  const [matchInfo, setMatchInfo] = useState(null);
  const [matchBank, setMatchBank] = useState('');
  const matchBankRef = useRef('');
  const [, setTick] = useState(0);
  // Cooldown labels count down without a reload.
  useEffect(() => { const t = setInterval(() => setTick((n) => n + 1), 30000); return () => clearInterval(t); }, []);
  const busy = Object.keys(busyKeys).find((k) => !k.startsWith('item:')) || '';
  const itemBusy = (item) => Object.keys(busyKeys).some((k) => k.startsWith(`item:${item.id}:`));
  const setResult = (itemId, r) => setResults((all) => ({ ...all, [itemId]: { ...r, at: new Date().toISOString() } }));
  const [message, setMessage] = useState(() => takeFlash());
  const [review, setReview] = useState([]);
  const [matches, setMatches] = useState([]);
  const [reviewLink, setReviewLink] = useState(false);
  const [entitlement, setEntitlement] = useState(null);
  useEffect(() => {
    if (!businessId) return;
    autocatAPI.settings(businessId).then(() => setReviewLink(true)).catch(() => setReviewLink(false));
  }, [businessId]);

  const loadMatches = useCallback(async () => {
    const mr = (await Promise.resolve().then(() => bankFeedsAPI.matchReview('open', matchBankRef.current || null)).catch(() => ({ data: [] }))).data;
    if (Array.isArray(mr)) { setMatches(mr); setMatchInfo(null); return; }
    setMatches((mr && mr.rows) || []);
    setMatchInfo(mr || null);
  }, []);

  const load = useCallback(async () => {
    const [i, a] = await Promise.all([bankFeedsAPI.items(businessId), accountsAPI.getAll(businessId ? { business_id: businessId } : {})]);
    // What this location's plan includes (upsell card, Connect button).
    setEntitlement(businessId ? (await Promise.resolve().then(() => bankFeedsAPI.entitlement(businessId)).catch(() => null))?.data || null : null);
    const list = i.data.items || [];
    setItems(list);
    // One business selected and nothing here: say whether the user has
    // connections in other businesses (only ones they may view are counted).
    if (businessId && !list.length) {
      const all = await Promise.resolve().then(() => bankFeedsAPI.items(null)).catch(() => null);
      setElsewhere(all ? (all.data.items || []).length : 0);
    } else setElsewhere(0);
    // Grouped targets and last-4 suggestions for connections this user manages.
    const h = {};
    await Promise.all(list.filter((it) => it.can_manage).map(async (it) => {
      // No business filter: the picker lists every business the user may
      // manage Bank Feeds for, grouped (the server decides which).
      const s = await Promise.resolve().then(() => bankFeedsAPI.suggestions(it.id)).then((r) => r.data).catch(() => null);
      if (s) h[it.id] = { groups: s.groups, byId: Object.fromEntries((s.accounts || []).map((x) => [x.id, x])) };
    }));
    setHints(h);
    setAccounts(Array.isArray(a.data) ? a.data : (a.data.accounts || []));
    setReview((await bankFeedsAPI.startingBalanceReview().catch(() => ({ data: [] }))).data || []);
    await loadMatches();
  }, [businessId, loadMatches]);

  useEffect(() => {
    matchBankRef.current = matchBank;
    if (loaded) loadMatches();
  }, [matchBank]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    (async () => {
      try {
        const s = await bankFeedsAPI.status();
        setStatus(s.data);
        if (!s.data.configured) return;
        await bankFeedsAPI.syncDue().catch(() => {});
        await load();
      } catch (e) { setMessage(formatApiError(e, 'Could not load bank feeds')); }
      finally { setLoaded(true); }
    })();
  }, [load]);

  // opts.item: the action belongs to that bank (only its buttons wait);
  // opts.onError: where its error is shown (default: the page message).
  const run = async (label, fn, { item = null, onError = null } = {}) => {
    const key = item ? `item:${item.id}:${label}` : label;
    setBusyKeys((b) => ({ ...b, [key]: true }));
    if (!item) setMessage('');
    try { await fn(); await load(); } catch (e) {
      const text = formatApiError(e, 'Something went wrong');
      if (onError) onError(text); else setMessage(text);
      // The action may still have finished on the server; show the real state.
      await load().catch(() => {});
    }
    setBusyKeys((b) => { const n = { ...b }; delete n[key]; return n; });
  };
  const itemError = (item) => (text) => setResult(item.id, { ok: false, kind: 'action', message: text });

  const reconnect = (item) => run('reauth', async () => {
    const msg = await startLink('reauth', { itemId: item.id });
    if (msg) setResult(item.id, { ok: true, kind: 'action', message: msg });
  }, { item, onError: itemError(item) });

  const sync = (item) => {
    const sb = syncButton(item);
    // Cooldown or weekly allowance used: say how long is left instead of ignoring the click.
    if (sb.cooldown) { setResult(item.id, { ok: false, info: true, kind: 'action', message: sb.wait }); return undefined; }
    return run('sync', async () => {
      const r = await bankFeedsAPI.sync(item.id);
      const d = r.data;
      if (d.needs_reconnect) setResult(item.id, { ok: false, kind: 'sync', message: 'Use Fix connection to sign in to this bank again first.' });
      else if (d.in_progress) {
        setResult(item.id, { ok: true, info: true, kind: 'sync', message: 'Still syncing. This can take a minute the first time; this card updates when it is done.' });
        const before = item.last_synced_at || null;
        const done = await waitForSync(() => bankFeedsAPI.items(businessId), item.id, before);
        setResult(item.id, done && done.last_error ? { ok: false, kind: 'sync', message: 'The sync did not finish. Try Sync now again.' }
          : done ? { ok: true, kind: 'sync', message: 'Sync finished.' } : { ok: true, info: true, kind: 'sync', message: 'Still syncing. Check back in a few minutes.' });
      }
      else if (d.stale) setResult(item.id, { ok: true, kind: 'sync', message: 'Another sync just finished.' });
      else setResult(item.id, { ok: true, kind: 'sync', message: syncSummary(d) });
    }, { item, onError: (text) => setResult(item.id, { ok: false, kind: 'sync', message: text }) });
  };

  const ignoreRest = (item) => {
    const rest = item.accounts.filter((a) => !a.account_id && !a.ignored);
    if (!rest.length) return undefined;
    if (!window.confirm(`Set ${rest.length} bank account${rest.length === 1 ? '' : 's'} (${rest.map((a) => a.name).join(', ')}) to "Don't import"? You can change this later.`)) return undefined;
    return run('rest', async () => {
      for (const pa of rest) await bankFeedsAPI.mapAccount(pa.id, { ignored: true });
    }, { item, onError: itemError(item) });
  };

  // Mapping: a pick is held for review; nothing is saved until Confirm.
  const savedChoice = (pa) => (pa.ignored ? 'ignore' : String(pa.account_id || ''));
  const cancelMap = (pa) => setPending((p) => { const n = { ...p }; delete n[pa.id]; return n; });
  const propose = (pa, value) => setPending((p) => {
    const n = { ...p };
    if (String(value) === savedChoice(pa)) delete n[pa.id]; else n[pa.id] = String(value);
    return n;
  });
  const confirmMap = (item, pa) => {
    const v = pending[pa.id];
    if (v === undefined) return undefined;
    return run(`map-${pa.id}`, async () => {
      await bankFeedsAPI.mapAccount(pa.id, mappingPayload(v));
      cancelMap(pa);
    }, { item, onError: itemError(item) });
  };

  const setStart = (pa) => run(`start-${pa.id}`, async () => {
    const p = (await bankFeedsAPI.startingBalance(pa.id)).data;
    if (p.has_reconciled) { setMessage('This account has reconciled rows; change its starting balance on the Reconciliation page.'); return; }
    const was = p.current_start ? ` (replaces ${money(p.current_start.opening_balance)} on ${p.current_start.opening_balance_date})` : '';
    if (!window.confirm(`Set the starting balance to ${money(p.opening_balance)} on ${p.opening_balance_date}${was}? The bank shows ${money(p.live_balance)} today.`)) return;
    const r = (await bankFeedsAPI.setStartingBalance(pa.id)).data;
    setMessage(`Starting balance set to ${money(r.opening_balance)} on ${r.opening_balance_date}. eloNest balance is now ${money(r.current_balance)}.`);
  });

  // Confirm dialog for moving a starting balance back to the bank's earliest
  // date: shows the old and new figures and how many rows start counting.
  const moveStart = (r) => run(`move-${r.plaid_account_ref}`, async () => {
    const p = (await bankFeedsAPI.moveStartPreview(r.plaid_account_ref)).data;
    const prop = p.anchor_proposal;
    if (!prop) { setMessage('Nothing to move: the bank has no rows before this starting balance.'); return; }
    if (prop.blocked.length) { setMessage(`Not moved: ${prop.blocked.map((k) => REVIEW_REASON[k] || k).join(' and ')}.`); return; }
    const was = p.current_start ? `${money(p.current_start.opening_balance)} on ${p.current_start.opening_balance_date}` : 'none';
    if (!window.confirm(`Move the starting balance from ${was} to ${money(p.opening_balance)} on ${p.opening_balance_date}?\n\n` +
      `${prop.rows_included} row${prop.rows_included === 1 ? '' : 's'} dated on or before the old start will then count. The figure is the bank's balance today (${money(p.live_balance)}) minus every row on the account.`)) return;
    const res = (await bankFeedsAPI.moveStart(r.plaid_account_ref, { opening_balance: p.opening_balance, opening_balance_date: p.opening_balance_date })).data;
    setMessage(`Starting balance moved to ${money(res.opening_balance)} on ${res.opening_balance_date}. eloNest balance is now ${money(res.current_balance)}.`);
  });

  const resolve = (m, action, transactionId) => run(`match-${m.id}`, async () => {
    await bankFeedsAPI.resolveMatch(m.id, { action, transaction_id: transactionId });
    setMessage({ link: 'Linked to the existing row; nothing added.', keep_both: 'Added as a separate row.', dismiss: 'Marked as a duplicate; nothing added.' }[action]);
  });

  // Mapping picks are saved only through confirmMap (above).
  const mayManage = (bid) => canManageBankFeeds(status?.permissions, bid);
  if (status && !status.configured) {
    return <div style={{ padding: 24 }}><h1>Bank feeds</h1><p>Bank feeds are not set up on this server yet.</p>{status.setup_problem && <p style={{ color: TONE.warn }}>{status.setup_problem}</p>}</div>;
  }
  if (status && !canViewBankFeeds(status.permissions, businessId)) {
    return <div style={{ padding: 24 }}><h1>Bank feeds</h1><p>You do not have access to Bank Feeds for this business. The owner can grant it on the Business page.</p></div>;
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>Bank feeds</h1>
        <ConnectBankButton businessId={businessId} permissions={status?.permissions || null} allowPersonal disabled={!!busy || (entitlement ? !entitlement.can_connect : false)}
          onConnected={async (msg) => { setMessage(msg); await load(); }} onError={setMessage} />
      </div>
      {status?.environment && status.environment !== 'production' && (
        <p style={{ color: TONE.warn }}>Test mode ({status.environment}): connections use Plaid's test banks.</p>
      )}
      {reviewLink && <p><a href="/category-review">Review categories for imported rows ?</a></p>}
      {message && <p role="status">{message}</p>}
      <PlanNotice entitlement={entitlement} busy={busy}
        onAddAddon={() => run('addon', () => bankFeedsAPI.addBankFeeds(businessId))} />
      {review.length > 0 && (
        <section data-testid="start-review" style={{ border: `1px solid ${TONE.warn}`, borderRadius: 8, padding: 16, marginTop: 16 }}>
          <strong>Starting balances to review</strong>
          {review.map((r) => (
            <div key={`${r.account_id}-${r.provisional ? "p" : "b"}`} style={{ marginTop: 8 }}>
              {r.account_name}: {money(r.opening_balance)} on {r.opening_balance_date}, {r.provisional ? 'needs confirming because' : 'not applied because'} {(r.reasons || r.review || []).map((k) => REVIEW_REASON[k] || k).join(' and ')}.{' '}
              {r.provisional
                ? <a href="/reconciliation" style={{ fontSize: 12 }}>Confirm or change on Reconciliation{r.opening_balance_note ? ` (note: ${r.opening_balance_note})` : ''}</a>
                : !mayManage(r.business_id) ? null
                : (r.reasons || r.review || []).includes('anchor_predates_bank')
                ? <button type="button" style={{ fontSize: 12 }} onClick={() => moveStart(r)} disabled={!!busy}>Review and move</button>
                : <button type="button" style={{ fontSize: 12 }} onClick={() => setStart({ id: r.plaid_account_ref })} disabled={!!busy || !r.plaid_account_ref}>Review and set</button>}
            </div>
          ))}
        </section>
      )}
      {(matches.length > 0 || matchBank) && (
        <section data-testid="match-review" style={{ border: `1px solid ${TONE.warn}`, borderRadius: 8, padding: 16, marginTop: 16 }}>
          <strong>Possible duplicates to review</strong>
          <div style={{ color: TONE.muted, fontSize: 13 }}>These bank rows look like rows already in eloNest. Nothing was added until you decide.</div>
          {matchInfo && Array.isArray(matchInfo.banks) && matchInfo.banks.length > 0 && (
            <label style={{ display: 'block', fontSize: 13, marginTop: 8 }}>Bank{' '}
              <select aria-label="Filter possible duplicates by bank" value={matchBank} onChange={(e) => setMatchBank(e.target.value)}>
                <option value="">All banks ({matchInfo.banks.reduce((s, b) => s + b.count, 0)})</option>
                {matchInfo.banks.map((b) => (
                  <option key={b.plaid_item_id ?? 'none'} value={b.plaid_item_id === null ? 'none' : String(b.plaid_item_id)}>
                    {b.institution_name || (b.plaid_item_id === null ? 'Accounts without a bank feed' : 'Bank')} ({b.count})
                  </option>
                ))}
              </select>
            </label>
          )}
          {matchCapText(matchInfo, matches.length) && <p role="status" data-testid="match-cap" style={{ color: TONE.warn, fontSize: 13 }}>{matchCapText(matchInfo, matches.length)}</p>}
          {matches.map((m) => (
            <MatchReviewCard key={m.id} match={m} busy={!!busy} onResolve={resolve} />
          ))}
          {!matches.length && <div style={{ fontSize: 13, marginTop: 8 }}>No possible duplicates for this bank.</div>}
        </section>
      )}
      {emptyText({ loaded, count: items.length, elsewhere }) && <p role="status">{emptyText({ loaded, count: items.length, elsewhere })}</p>}
      {items.map((item) => (
        <section key={item.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <div>
              <strong>{item.institution_name || 'Bank'}</strong>
              {(() => { const h = syncHealth(item); return (
                <span role="status" aria-label={`Sync health: ${h.label}`} title={h.detail || undefined} style={{ color: TONE[h.tone], marginLeft: 8, fontSize: 13 }}>
                  <span aria-hidden="true" style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 4, background: TONE[h.tone], marginRight: 4 }} />
                  {h.label}
                </span>); })()}
              {!item.paused && syncHealth(item).detail && syncHealth(item).tone !== 'muted' && <div style={{ color: TONE.muted, fontSize: 13 }}>{syncHealth(item).detail}</div>}
              {item.paused && <div style={{ color: TONE.muted, fontSize: 13 }}>{pausedText(item)}</div>}
              <div style={{ color: TONE.muted, fontSize: 13 }}>
                {item.last_synced_at ? `Last synced ${new Date(item.last_synced_at).toLocaleString()}` : 'Not synced yet'}
              </div>
              {(() => { const r = lastSyncLine(item, results[item.id]); return r ? (
                <div role="status" data-testid={`sync-result-${item.id}`} style={{ color: TONE[r.tone], fontSize: 13, maxWidth: 640, whiteSpace: 'normal' }}>{r.text}</div>) : null; })()}
            </div>
            {item.can_manage && <div style={{ display: 'flex', gap: 8 }}>
              {/* Update mode: repairs this connection in place (same connection, no new slot). */}
              {(() => { const cb = connectionButton(item); return (
                <button type="button" title={cb.title} onClick={() => reconnect(item)} disabled={!!busy || itemBusy(item)}
                  style={cb.needsAttention ? { color: TONE.warn, fontWeight: 600 } : undefined}>
                  {cb.label}
                </button>); })()}
              {(() => { const sb = syncButton(item); return <button type="button" onClick={() => sync(item)} disabled={!!busy || itemBusy(item) || sb.disabled} title={sb.title}>{busyKeys[`item:${item.id}:sync`] ? 'Syncing…' : sb.label}</button>; })()}
              {item.can_refresh_from_bank && <button type="button" title="Ask the bank for its newest transactions now; Sync imports them" onClick={() => run('refresh', () => bankFeedsAPI.refreshFromBank(item.id), { item, onError: itemError(item) })} disabled={!!busy || itemBusy(item)}>Refresh from bank</button>}
              <button type="button" onClick={() => run('balances', () => bankFeedsAPI.balances(item.id), { item, onError: itemError(item) })} disabled={!!busy || itemBusy(item)}>Refresh balances</button>
            </div>}
          </div>
          {item.can_manage && item.accounts.some((a) => !a.account_id && !a.ignored) && item.accounts.some((a) => a.account_id || a.ignored) && (
            <button type="button" style={{ marginTop: 8 }} onClick={() => ignoreRest(item)} disabled={!!busy || itemBusy(item)}>Don't import the rest</button>
          )}
          <table style={{ width: '100%', marginTop: 12, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: TONE.muted, fontSize: 13 }}>
                <th>Bank account</th><th>eloNest account</th><th>Bank balance</th><th>eloNest balance</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {item.accounts.map((pa) => {
                const s = balanceStatus(pa.balance);
                const hint = hints[item.id];
                const sug = !pa.account_id && !pa.ignored ? hint?.byId[pa.id]?.suggested : null;
                const otherBiz = pa.account_id && pa.account_business_name && Number(pa.account_business_id) !== Number(item.business_id) ? pa.account_business_name : null;
                return (
                  <tr key={pa.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                    <td>{pa.name}{pa.mask ? ` ••${pa.mask}` : ''} <span style={{ color: TONE.muted }}>({pa.plaid_label || pa.plaid_subtype || pa.plaid_type})</span></td>
                    <td>
                      <MappingSelect pa={pa} accounts={accounts} groups={hint?.groups} suggestion={hint?.byId[pa.id]} businessId={item.business_id}
                        value={pending[pa.id] !== undefined ? pending[pa.id] : savedChoice(pa)}
                        onChange={(v) => propose(pa, v)} disabled={!!busy || itemBusy(item) || !item.can_manage} allowCreate={!pa.account_id} mapped={Boolean(pa.account_id || pa.ignored)} />
                      {otherBiz && <div style={{ color: TONE.muted, fontSize: 12 }}>In {otherBiz}</div>}
                      {pending[pa.id] !== undefined && item.can_manage && (
                        <div data-testid={`confirm-map-${pa.id}`} style={{ fontSize: 12, marginTop: 4, padding: 6, border: '1px solid #d1d5db', borderRadius: 6, background: '#f9fafb' }}>
                          Review: {pa.name}{pa.mask ? ` ending ${pa.mask}` : ''} will {choiceText(pending[pa.id], hint?.groups, accounts)}.{' '}
                          <button type="button" style={{ fontSize: 12 }} onClick={() => confirmMap(item, pa)} disabled={!!busy || itemBusy(item)}>Confirm</button>{' '}
                          <button type="button" style={{ fontSize: 12 }} onClick={() => cancelMap(pa)}>Cancel</button>
                        </div>
                      )}
                      {sug && item.can_manage && (
                        <div style={{ fontSize: 12 }}>
                          Suggested: {sug.account_name}{sug.business_name || businessOf(hint.groups, sug.account_id) ? ` (${sug.business_name || businessOf(hint.groups, sug.account_id)})` : ''}{' '}
                          <button type="button" style={{ fontSize: 12 }} onClick={() => propose(pa, String(sug.account_id))} disabled={!!busy || itemBusy(item)}>Use</button>
                        </div>
                      )}
                    </td>
                    <td>{pa.balance ? money(pa.balance.live_balance) : money(pa.balance_current)}</td>
                    <td>{pa.balance ? money(pa.balance.ledger_balance) : '—'}</td>
                    <td style={{ color: TONE[s.tone] }}>
                      {pa.account_id ? s.text : (pa.ignored ? 'Not imported' : 'Pick an account')}
                      {(() => { const ns = nextStep(item, pa); if (!ns) return null; return (
                        <div style={{ color: TONE.muted, fontSize: 12 }}>
                          {ns.text}
                          {item.can_manage && ns.kind === 'sync' && !syncButton(item).disabled && <div><button type="button" style={{ fontSize: 12 }} onClick={() => sync(item)} disabled={!!busy || itemBusy(item)}>Sync now</button></div>}
                          {item.can_manage && ns.kind === 'pick' && <div><button type="button" style={{ fontSize: 12 }} onClick={() => propose(pa, 'ignore')} disabled={!!busy || itemBusy(item)}>Don't import this account</button></div>}
                        </div>); })()}
                      {(() => { const g = gapBreakdown(pa); if (!g) return null; return (
                        <details style={{ fontSize: 12, color: '#374151' }}>
                          <summary>Why the difference?</summary>
                          <div>Bank {g.bank} vs eloNest {g.elonest}: {g.difference}. {g.direction}</div>
                          <ul style={{ margin: '4px 0', paddingLeft: 16 }}>{g.lines.map((l) => <li key={l}>{l}</li>)}</ul>
                          {item.can_manage && (
                            <div>Checked these? As a last resort,{' '}
                              <button type="button" onClick={() => setStart(pa)} disabled={!!busy || itemBusy(item)}
                                style={{ fontSize: 12, background: 'none', border: 'none', padding: 0, color: TONE.muted, textDecoration: 'underline', cursor: 'pointer' }}>set the starting balance from the bank</button>
                              {' '}(adjusts the opening figure so the balances agree).
                            </div>
                          )}
                        </details>); })()}
                      {pa.pending_count ? <div style={{ color: TONE.muted, fontSize: 12 }}>{pa.pending_count} pending (not in balances yet)</div> : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {item.can_manage && (
            <div style={{ borderTop: '1px solid #f3f4f6', marginTop: 12, paddingTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setDisconnecting(item)} disabled={!!busy || itemBusy(item)}
                style={{ color: TONE.bad, border: `1px solid ${TONE.bad}`, background: '#fff', borderRadius: 4, padding: '2px 10px' }}>Disconnect bank</button>
            </div>
          )}
        </section>
      ))}
      <ConfirmDisconnect message={disconnecting ? disconnectMessage(disconnecting) : null} busy={!!busy}
        onCancel={() => setDisconnecting(null)}
        onConfirm={() => { const it = disconnecting; setDisconnecting(null); run('rm', () => bankFeedsAPI.remove(it.id), { item: it }); }} />
    </div>
  );
}
